import {
  IconAlertTriangle,
  IconArrowLeft,
  IconKeyboard,
  IconMicrophone,
  IconNote,
  IconPencil,
  IconPhone,
  IconPlayerStopFilled,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TopbarTitle } from '@/components/layout';
import { LANGUAGE_NAME, type Language } from '@/i18n';
import { navigate } from '@/lib/router';
import type { Patient } from '@/types/api';
import { allergyLabel } from '@/utils/allergies';
import { formatDate } from './format';
import { HealthSheet } from './HealthSheet';

/** Back button and name, in the top bar right of the logo, so the chart starts higher. */
export function PatientHeader({ patient: p }: { patient: Patient }) {
  const { t } = useTranslation();
  return (
    <TopbarTitle>
      <button
        type="button"
        className="icon-btn"
        onClick={() => navigate({ name: 'patients' })}
        aria-label={t('chart.header.back')}
      >
        <IconArrowLeft size={24} />
      </button>
      <h1>
        {p.lastName} {p.firstName}
      </h1>
    </TopbarTitle>
  );
}

/** Left column: allergies and diseases first, then notes and the contact details. */
export function PatientInfo({
  patient: p,
  onSaved,
}: {
  patient: Patient;
  onSaved: (patient: Patient) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const born = p.birthDate ? formatDate(p.birthDate) : t('chart.header.noBirthDate');
  return (
    <section className="card patient-info">
      <div className="patient-info__health">
        <div className="row">
          <span className="field__label grow">{t('chart.health.title')}</span>
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            onClick={() => setEditing(true)}
            aria-label={t('chart.health.edit')}
            title={t('chart.health.edit')}
          >
            <IconPencil size={16} />
          </button>
        </div>
        {p.allergies.length > 0 && (
          <div className="patient-info__allergies" role="note">
            <IconAlertTriangle size={18} aria-hidden="true" />
            <div>
              <strong>{t('chart.health.allergies')}</strong>
              <p>{p.allergies.map(allergyLabel).join(', ')}</p>
            </div>
          </div>
        )}
        {p.diagnoses.length > 0 && (
          <ul className="icd-list icd-list--compact">
            {p.diagnoses.map((d) => (
              <li key={d.code} className="icd-list__item">
                <span className="icd-code">{d.code}</span>
                <span>{d.name}</span>
              </li>
            ))}
          </ul>
        )}
        {!(p.allergies.length || p.diagnoses.length) && (
          <p className="small muted">{t('chart.health.none')}</p>
        )}
      </div>
      {p.notes && (
        <div className="patient-info__alert">
          <IconNote size={18} aria-hidden="true" />
          <div>
            <div className="field__label">{t('chart.info.notes')}</div>
            <p>{p.notes}</p>
          </div>
        </div>
      )}
      <dl className="facts">
        <dt>{t('chart.info.born')}</dt>
        <dd>{born}</dd>
        <dt>{t('chart.info.phone')}</dt>
        <dd>
          {p.phone ? (
            <a href={`tel:${p.phone.replace(/\s+/g, '')}`} className="patient-info__phone">
              <IconPhone size={16} aria-hidden="true" /> {p.phone}
            </a>
          ) : (
            '—'
          )}
        </dd>
      </dl>
      {editing && <HealthSheet patient={p} onClose={() => setEditing(false)} onSaved={onSaved} />}
    </section>
  );
}

type FabProps = {
  listening: boolean;
  supported: boolean;
  typing: boolean;
  /** Viewing an earlier visit: nothing can be recorded. */
  locked?: boolean;
  onMic: () => void;
  onTyping: () => void;
  /** Dictation language; tapping the chip switches to the next one. */
  language: Language;
  onLanguage: () => void;
};

function micLabel(p: { listening: boolean; supported: boolean }, t: TFunction): string {
  if (!p.supported) return t('chart.dock.keyboardDictation');
  return p.listening ? t('chart.dock.stop') : t('chart.dock.start');
}

/** Floating controls in the bottom right: typing and the microphone. */
export function ChartFab(p: FabProps) {
  const { t } = useTranslation();
  return (
    <div className="fab" role="toolbar" aria-label={t('chart.dock.label')}>
      <button
        type="button"
        className="fab__btn"
        aria-pressed={p.typing}
        onClick={p.onTyping}
        disabled={p.locked}
        aria-label={t('chart.dock.typing')}
        title={t('chart.dock.typing')}
      >
        <IconKeyboard size={24} />
      </button>
      <div className="mic-wrap">
        <button
          type="button"
          className={`mic ${p.listening ? 'mic--live' : ''}`}
          onClick={p.onMic}
          disabled={p.locked}
          aria-pressed={p.listening}
          aria-label={micLabel(p, t)}
        >
          {p.listening ? <IconPlayerStopFilled size={30} /> : <IconMicrophone size={32} />}
        </button>
        <button
          type="button"
          className="mic-wrap__lang"
          onClick={p.onLanguage}
          disabled={p.locked || p.listening}
          aria-label={t('chart.dock.dictationLanguage', { lang: LANGUAGE_NAME[p.language] })}
          title={t('chart.dock.dictationLanguage', { lang: LANGUAGE_NAME[p.language] })}
        >
          {p.language.toUpperCase()}
        </button>
      </div>
    </div>
  );
}
